import { useMemo } from 'react';

/**
 * Convert HSL color to hex format
 */
function hslToHex(h: number, s: number, l: number): string {
	l /= 100;
	const a = (s * Math.min(l, 1 - l)) / 100;
	const f = (n: number) => {
		const k = (n + h / 30) % 12;
		const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
		return Math.round(255 * color)
			.toString(16)
			.padStart(2, '0');
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Generate a consistent color for a user based on their ID.
 * Uses HSL color space for vibrant, distinguishable colors.
 *
 * @param userId - The user's unique identifier
 * @param format - Color format: 'hsl' or 'hex' (default: 'hsl')
 * @returns A CSS color string in the specified format
 *
 * @example
 * ```tsx
 * const userColor = useUserColor(currentUser.id);
 * // Returns: "hsl(240, 70%, 60%)"
 *
 * const hexColor = useUserColor(currentUser.id, 'hex');
 * // Returns: "#6666ff"
 * ```
 */
export function useUserColor(userId: string, format: 'hsl' | 'hex' = 'hsl'): string {
	return useMemo(() => {
		let hash = 0;
		for (let i = 0; i < userId.length; i++) {
			hash = userId.charCodeAt(i) + ((hash << 5) - hash);
		}
		const hue = Math.abs(hash % 360);

		if (format === 'hex') {
			return hslToHex(hue, 70, 60);
		}

		return `hsl(${hue}, 70%, 60%)`;
	}, [userId, format]);
}
